import React, { useRef } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import classnames from 'classnames'
import type { QuoteFile } from 'paintscout'
import type { StyleClasses } from '../../styles'
import { generateSrcSet } from '../../util'

const useStyles = makeStyles<Theme, ResponsiveImageProps>(
  (_theme) => ({
    root: {
      color: 'blue',
      objectFit: 'contain'
    },
    image: {
      maxWidth: '100%',
      height: 'auto',
      maxHeight: '100%',
      objectFit: 'contain'
    }
  }),
  { name: 'ResponsiveImage' }
)

export interface ResponsiveImageProps {
  classes?: StyleClasses<typeof useStyles>
  file: QuoteFile
  size?: number
  page?: number
  onClick: (ev: any) => any
  omitSizes?: string[]
}

function ResponsiveImage(props: ResponsiveImageProps) {
  const classes = useStyles(props)
  const { file, onClick, size, omitSizes } = props
  const imageRef = useRef(null)

  // Need to organize or src url here for image specific sizing and type demands
  if (file.type.includes('svg')) {
    const generatedSrc = generateSrcSet({ file, svg: true })
    return (
      <>
        <img
          ref={imageRef}
          className={classnames(classes.fileRoot, classes.image, classes.root)}
          src={generatedSrc}
          onClick={onClick}
          width={'auto'}
        />
      </>
    )
  }

  if (size) {
    // Maybe not?
    const generatedSrc = generateSrcSet({ file, size })
    return (
      <>
        <img
          ref={imageRef}
          className={classnames(classes.fileRoot, classes.image)}
          src={generatedSrc}
          onClick={onClick}
        />
      </>
    )
  }

  const generatedSrc = generateSrcSet({ file })
  const allSizes = [
    { media: '(max-width: 400px)', size: '300px' },
    { media: '(max-width: 600px)', size: '500px' },
    { media: '(max-width: 800px)', size: '700px' },
    { media: '(max-width: 1000px)', size: '900px' },
    { media: '', size: '1200px' }
  ]
  const filteredSizes = allSizes
    .filter(({ size }) => !omitSizes?.includes(size))
    .map(({ media, size }) => (media ? `${media} ${size}` : size))
    .join(',\n')

  return (
    <>
      <img
        ref={imageRef}
        srcSet={generatedSrc}
        sizes={filteredSizes}
        src={file.src}
        onError={(e) => {
          // If the Lambda-resized size isn't ready yet, fall back to the original upload
          if (e.currentTarget.srcset) {
            e.currentTarget.srcset = ''
          }
        }}
        className={classnames(classes.fileRoot, classes.image)}
        onClick={onClick}
      />
    </>
  )
}

export default ResponsiveImage
